// Maps an AI document-extraction result onto schema field names.
//
// The /ai service returns one normalised object per document — for an Emirates
// ID: `ID_Number`, `Name`, `Date_of_Birth`, `Nationality`, `Sex`, `Expiry_Date`
// — and the upload screen namespaces the other two document types under
// `license.` and `vehicle.`. Each field declares the aliases it accepts once,
// in the journey config, instead of every screen re-implementing the lookup.
//
// Some fields cannot be read straight across. The AI returns a single `Name`
// where the traveller and pet-owner forms ask for first / middle / last, so a
// field may also declare an `ocrTransform` naming one of the extractors below.

import { allFields } from "./schema";

/** Read "license.Expiry_Date" out of a nested extraction result. */
const readPath = (source, path) =>
  String(path)
    .split(".")
    .reduce((node, key) => (node == null ? undefined : node[key]), source);

/**
 * Split a full name the way a UAE Emirates ID presents it: given name first,
 * family name last, everything in between treated as middle names.
 *
 * @param {string} value
 * @returns {{ first: string, middle: string, last: string }}
 */
export const splitName = (value) => {
  const parts = String(value || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first: "", middle: "", last: "" };
  if (parts.length === 1) return { first: parts[0], middle: "", last: "" };
  if (parts.length === 2) return { first: parts[0], middle: "", last: parts[1] };
  return {
    first: parts[0],
    middle: parts.slice(1, -1).join(" "),
    last: parts[parts.length - 1],
  };
};

/**
 * Named value transforms a field can apply to what it read.
 * @type {Record<string, (value: *) => *>}
 */
const TRANSFORMS = {
  firstName: (v) => splitName(v).first,
  middleName: (v) => splitName(v).middle,
  lastName: (v) => splitName(v).last,
  // Plate numbers and IATA-style codes read better upper-cased, and some
  // carriers reject the lower-case form outright.
  upper: (v) => String(v ?? "").toUpperCase(),
  digits: (v) => String(v ?? "").replace(/\D/g, ""),
  /** "2019" out of "2019-03-04" — the mulkiya's year of manufacture. */
  year: (v) => {
    const match = /\b(19|20)\d{2}\b/.exec(String(v ?? ""));
    return match ? match[0] : String(v ?? "");
  },
};

/** Register a project-specific transform without editing this file. */
export const registerOcrTransform = (name, fn) => {
  TRANSFORMS[name] = fn;
};

/**
 * First non-empty value among a field's declared OCR aliases.
 * @param {Record<string, *>} ocr
 * @param {string} keys  comma-separated alias list
 */
const firstMatch = (ocr, keys) => {
  for (const key of String(keys).split(",")) {
    const value = readPath(ocr, key.trim());
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return undefined;
};

/**
 * Build the patch of values a document extraction can pre-fill.
 *
 * Only fields that declare `ocrKeys` participate, so adding OCR support for a
 * new field is a one-line config change and never touches a component.
 *
 * @param {import("./types").FormSchema} schema
 * @param {Record<string, *> | null | undefined} ocr
 * @returns {Record<string, *>}
 */
export const ocrPrefill = (schema, ocr) => {
  if (!ocr || !schema) return {};
  /** @type {Record<string, *>} */
  const patch = {};

  for (const field of allFields(schema)) {
    if (!field.ocrKeys) continue;

    const raw = firstMatch(ocr, field.ocrKeys);
    if (raw === undefined) continue;

    const transform = field.ocrTransform ? TRANSFORMS[field.ocrTransform] : null;
    if (field.ocrTransform && !transform) {
      if (process.env.NODE_ENV !== "production") {
        console.error(
          `[form] Unknown ocrTransform "${field.ocrTransform}" on field "${field.name}"`
        );
      }
    }

    const value = transform ? transform(raw) : raw;
    // A transform can legitimately produce nothing — a two-word name has no
    // middle name — and an empty pre-fill is worse than none, because it looks
    // like the document was read and the field was genuinely blank.
    if (value === undefined || value === null || value === "") continue;

    patch[field.name] = value;
  }

  return patch;
};

/** How many fields a given extraction actually filled — drives the "we
 *  pre-filled N fields" notice, so it never claims success on an empty read. */
export const ocrPrefillCount = (schema, ocr) => Object.keys(ocrPrefill(schema, ocr)).length;

export default ocrPrefill;
